// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

// doc-exempt: a copy of AYNI 3D's src/renderer/ui/ai-voice-mode.ts; never edit it here (scripts/sync-ayni3d-copy.ts)
/**
 * Voice mode: talking with the assistant instead of typing
 * (docs/AI_CHATBOT_VOICE.md). One tap starts it; the mic hears when the
 * question is over, the words become an ordinary turn, and the reply is read
 * aloud. Hands-free (the presenter, a controller, the website, a headset), it
 * then listens again, until it is tapped off or nobody speaks; a kiosk, where
 * a crowd talks around the mic, takes one question per tap. A tap while it
 * speaks interrupts it; a tap while it listens sends at once.
 *
 * No DOM here: the surface supplies the mic, the speech endpoint, the turn
 * and the voice, and draws the phase (the AI tab's mic, AYNI XR's headset bar).
 */

export type VoicePhase = 'off' | 'listening' | 'transcribing' | 'thinking' | 'speaking';

/** Why voice mode went off, when it did not just finish: shown to the visitor. */
export type VoiceEndReason = 'quiet' | 'mic-denied' | 'error';

export interface VoiceModeHost {
  /** Starts the mic; rejects when it is refused. The recorder calls `onEnd` when the question is over. */
  record(onEnd: (reason: 'speech-end' | 'no-speech') => void): Promise<void>;
  /** Stops the mic: the WAV, or null when nothing was said. */
  stopRecording(): Promise<Uint8Array | null>;
  cancelRecording(): void;
  transcribe(wav: Uint8Array): Promise<{ ok: true; text: string } | { ok: false; code: string }>;
  /** Asks the question as an ordinary turn; the reply's text once the turn is over, or null when it failed or was stopped. */
  ask(text: string): Promise<string | null>;
  /** Reads the reply aloud; resolves once it has been read or cut short. */
  speak(text: string): Promise<void>;
  stopSpeaking(): void;
  cancelTurn(): void;
  /** Whether a transcript is only noise (ai-speech-io.ts isPhantomTranscript). */
  isNoise(text: string): boolean;
  /** The phase changed; `ended` says why voice mode went off, `code` the error when there was one. */
  onPhase(phase: VoicePhase, detail?: { ended?: VoiceEndReason; code?: string }): void;
}

export interface VoiceModeOptions {
  /** Listen again after each reply (true), or take one question per tap (a kiosk). */
  handsFree: boolean;
}

export class VoiceMode {
  private current: VoicePhase = 'off';
  /** Bumped by every stop, so a step that finishes late does nothing. */
  private run = 0;
  private finishing = false;

  constructor(private readonly host: VoiceModeHost, private options: VoiceModeOptions) {}

  public get phase(): VoicePhase {
    return this.current;
  }

  public setHandsFree(handsFree: boolean): void {
    this.options = { ...this.options, handsFree };
  }

  /** The mic button: start, send now, interrupt, or stop, depending on the phase. */
  public tap(): void {
    switch (this.current) {
      case 'off':
        void this.listen(this.run);
        break;
      case 'listening':
        void this.finishListening(this.run);
        break;
      case 'speaking': {
        // Interrupt: stop reading, and listen for the next question.
        this.host.stopSpeaking();
        const run = ++this.run;
        if (this.options.handsFree) void this.listen(run);
        else this.setPhase('off');
        break;
      }
      case 'transcribing':
      case 'thinking':
        this.stop();
        break;
    }
  }

  /** Off, whatever it was doing: the mic closes, the turn stops, the voice falls silent. */
  public stop(): void {
    const was = this.current;
    this.run++;
    this.finishing = false;
    if (was === 'listening') this.host.cancelRecording();
    if (was === 'thinking') this.host.cancelTurn();
    if (was === 'speaking') this.host.stopSpeaking();
    if (was !== 'off') this.setPhase('off');
  }

  private setPhase(phase: VoicePhase, detail?: { ended?: VoiceEndReason; code?: string }): void {
    this.current = phase;
    this.host.onPhase(phase, detail);
  }

  private end(run: number, ended?: VoiceEndReason, code?: string): void {
    if (run !== this.run) return;
    this.run++;
    this.finishing = false;
    this.setPhase('off', ended ? { ended, ...(code ? { code } : {}) } : undefined);
  }

  private async listen(run: number): Promise<void> {
    if (run !== this.run) return;
    this.setPhase('listening');
    try {
      await this.host.record((reason) => {
        if (reason === 'no-speech') {
          this.host.cancelRecording();
          this.end(run, 'quiet');
        } else {
          void this.finishListening(run);
        }
      });
    } catch {
      this.end(run, 'mic-denied');
    }
  }

  private async finishListening(run: number): Promise<void> {
    if (run !== this.run || this.current !== 'listening' || this.finishing) return;
    this.finishing = true;
    const wav = await this.host.stopRecording().catch(() => null);
    if (run !== this.run) return;
    this.finishing = false;
    if (!wav) {
      this.end(run, 'quiet');
      return;
    }
    this.setPhase('transcribing');
    const heard = await this.host.transcribe(wav).catch(() => ({ ok: false as const, code: 'AI_UNAVAILABLE' }));
    if (run !== this.run) return;
    if (!heard.ok) {
      this.end(run, 'error', heard.code);
      return;
    }
    if (this.host.isNoise(heard.text)) {
      // Noise, not a question: listen again (hands-free) or stop quietly.
      if (this.options.handsFree) void this.listen(run);
      else this.end(run, 'quiet');
      return;
    }
    this.setPhase('thinking');
    const reply = await this.host.ask(heard.text.trim());
    if (run !== this.run) return;
    if (!reply) {
      this.end(run);
      return;
    }
    this.setPhase('speaking');
    await this.host.speak(reply);
    if (run !== this.run) return;
    if (this.options.handsFree) void this.listen(run);
    else this.end(run);
  }
}
